import { useCallback, useEffect, useRef, useState } from 'react';
import { BUILD_ID } from '../../version';
import useLanguage from '../../hooks/useLanguage';
import useTheme from '../../hooks/useTheme';
import {
  checkWebsiteVersion, clearWebsiteReloadMarker, fetchWebsiteVersion, hasAttemptedWebsiteReload,
  hasWebsiteUpdate, hasWebsiteUpdateInteraction, isWebsiteUpdateBrowsingRoute,
  reloadWebsiteVersion, rememberWebsiteVersion, WEBSITE_NAVIGATION_EVENT,
} from '../../utils/websiteVersion';
import WebsiteVersionDialog from './WebsiteVersionDialog';
import XiaoyingLoading from './XiaoyingLoading';

const replaceLocation = url => window.location.replace(url);
export const WEBSITE_CHECK_INTERVAL = 5 * 60_000;
const WEBSITE_CHECK_MIN_INTERVAL = 60_000;
export const WEBSITE_AUTO_UPDATE_DELAY = 5_000;

export default function WebsiteVersionGate({ children, reload = replaceLocation }) {
  const { language } = useLanguage();
  useTheme();
  const [status, setStatus] = useState(BUILD_ID ? 'checking' : 'development');
  const [notice, setNotice] = useState(null);
  const [automatic, setAutomatic] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateFailed, setUpdateFailed] = useState(false);
  const pendingRef = useRef(null);
  const dismissedRef = useRef('');
  const interactedRef = useRef(false);
  const automaticTimer = useRef(null);
  const updatingRef = useRef(false);
  const refreshTimeout = useRef(null);
  const lastCheck = useRef(0);
  const previousRoute = useRef({ pathname: window.location.pathname, search: window.location.search });
  const cancelAutomatic = useCallback(() => {
    window.clearTimeout(automaticTimer.current);
    automaticTimer.current = null;
    setAutomatic(false);
  }, []);
  const refresh = useCallback((buildId, automatic = false) => {
    if (updatingRef.current) return;
    const failed = () => {
      updatingRef.current = false;
      setUpdating(false);
      setUpdateFailed(true);
      setConfirmUpdate(false);
    };
    updatingRef.current = true;
    setUpdating(true);
    setUpdateFailed(false);
    try {
      if (!reloadWebsiteVersion(buildId, { automatic, reload })) { failed(); return; }
      // A cancelled beforeunload prompt or failed navigation must not leave a disabled notice forever.
      refreshTimeout.current = window.setTimeout(failed, 6500);
    } catch { failed(); }
  }, [reload]);
  useEffect(() => () => window.clearTimeout(refreshTimeout.current), []);
  useEffect(() => {
    if (!BUILD_ID) return undefined;
    const controller = new AbortController();
    let active = true;
    const timeout = window.setTimeout(() => controller.abort(), 4000);
    checkWebsiteVersion({ signal: controller.signal, reload }).then(result => {
      if (!active) return;
      window.clearTimeout(timeout);
      if (result !== 'reloading') clearWebsiteReloadMarker();
      lastCheck.current = Date.now();
      setNotice(result === 'outdated' ? 'outdated' : result === 'current' && hasWebsiteUpdate(BUILD_ID) ? 'updated' : null);
      setStatus(result);
    });
    return () => { active = false; window.clearTimeout(timeout); controller.abort(); };
  }, [reload]);

  useEffect(() => {
    if (!BUILD_ID || ['checking', 'reloading', 'development'].includes(status)) return undefined;
    let active = true;
    let request = null;
    const browsing = () => isWebsiteUpdateBrowsingRoute(window.location.pathname, window.location.search);
    const canUpdateAutomatically = () => navigator.onLine !== false && document.visibilityState !== 'hidden' && browsing()
      && !interactedRef.current && !hasWebsiteUpdateInteraction() && status !== 'outdated'
      && !document.querySelector('.website-version__panel:hover, .website-version__panel:focus-within');
    const offer = latest => {
      if (pendingRef.current && pendingRef.current.buildId !== latest.buildId) cancelAutomatic();
      pendingRef.current = latest;
      if (dismissedRef.current === latest.buildId) return;
      setNotice('available');
      if (automaticTimer.current || updatingRef.current || hasAttemptedWebsiteReload(latest.buildId) || !canUpdateAutomatically()) return;
      setAutomatic(true);
      automaticTimer.current = window.setTimeout(() => {
        cancelAutomatic();
        if (!active || !canUpdateAutomatically() || dismissedRef.current === latest.buildId) return;
        refresh(latest.buildId, true);
      }, WEBSITE_AUTO_UPDATE_DELAY);
    };
    const check = async (force = false) => {
      if (!active || request || updatingRef.current || document.visibilityState === 'hidden') return;
      if (!force && Date.now() - lastCheck.current < WEBSITE_CHECK_MIN_INTERVAL) return;
      lastCheck.current = Date.now();
      const controller = new AbortController();
      request = controller;
      const timeout = window.setTimeout(() => controller.abort(), 4000);
      const latest = await fetchWebsiteVersion({ signal: controller.signal });
      window.clearTimeout(timeout);
      if (request === controller) request = null;
      if (!active) return;
      if (controller.signal.aborted || !latest) { cancelAutomatic(); return; }
      if (latest.buildId !== BUILD_ID) offer(latest);
      else if (pendingRef.current) {
        pendingRef.current = null;
        cancelAutomatic();
        setNotice(current => current === 'available' ? null : current);
        setConfirmUpdate(false);
      }
    };
    const interact = event => {
      cancelAutomatic();
      if (!event.target.closest?.('.website-version')) interactedRef.current = true;
    };
    const navigate = () => {
      const previous = previousRoute.current;
      const changed = previous.pathname !== window.location.pathname || previous.search !== window.location.search;
      if (!changed) return;
      cancelAutomatic();
      // Leaving an editor/form may still have a save in flight; this transition is never auto-refreshed.
      interactedRef.current = !isWebsiteUpdateBrowsingRoute(previous.pathname, previous.search) || !browsing();
      previousRoute.current = { pathname: window.location.pathname, search: window.location.search };
      dismissedRef.current = '';
      setConfirmUpdate(false);
      setUpdateFailed(false);
      if (pendingRef.current) offer(pendingRef.current);
      void check();
    };
    const resume = () => {
      if (document.visibilityState === 'hidden') cancelAutomatic();
      else void check();
    };
    const interval = window.setInterval(() => { void check(); }, WEBSITE_CHECK_INTERVAL);
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    window.addEventListener('offline', cancelAutomatic);
    window.addEventListener(WEBSITE_NAVIGATION_EVENT, navigate);
    document.addEventListener('visibilitychange', resume);
    document.addEventListener('pointerdown', interact, true);
    document.addEventListener('keydown', interact, true);
    document.addEventListener('input', interact, true);
    document.addEventListener('wheel', interact, { passive: true });
    // A failed entry-time refresh still gets a manual update action after discovery.
    if (status === 'outdated') void check(true);
    return () => {
      active = false;
      request?.abort();
      window.clearInterval(interval);
      window.clearTimeout(automaticTimer.current);
      automaticTimer.current = null;
      window.removeEventListener('focus', resume);
      window.removeEventListener('online', resume);
      window.removeEventListener('offline', cancelAutomatic);
      window.removeEventListener(WEBSITE_NAVIGATION_EVENT, navigate);
      document.removeEventListener('visibilitychange', resume);
      document.removeEventListener('pointerdown', interact, true);
      document.removeEventListener('keydown', interact, true);
      document.removeEventListener('input', interact, true);
      document.removeEventListener('wheel', interact);
    };
  }, [cancelAutomatic, refresh, status]);

  const dismiss = useCallback(() => {
    if (updatingRef.current) return;
    cancelAutomatic();
    if (pendingRef.current) dismissedRef.current = pendingRef.current.buildId;
    else if (status === 'current') rememberWebsiteVersion(BUILD_ID);
    setConfirmUpdate(false);
    setNotice(null);
  }, [cancelAutomatic, status]);

  const update = () => {
    if (!pendingRef.current || updatingRef.current) return;
    cancelAutomatic();
    if (!confirmUpdate && (interactedRef.current || !isWebsiteUpdateBrowsingRoute(window.location.pathname, window.location.search) || hasWebsiteUpdateInteraction())) {
      setConfirmUpdate(true);
      return;
    }
    refresh(pendingRef.current.buildId);
  };

  if (status === 'checking' || status === 'reloading') {
    return <XiaoyingLoading label="正在打开小影" detail={status === 'reloading' ? '正在载入最新版本' : '正在准备创作空间'} />;
  }
  return <>
    {children}
    {notice && <WebsiteVersionDialog
      outdated={notice === 'outdated'} available={notice === 'available'}
      automatic={automatic} confirmUpdate={confirmUpdate} updating={updating} updateFailed={updateFailed} onUpdate={update}
      english={language === 'en-US'}
      onClose={dismiss}
    />}
  </>;
}
